import { PreBuiltGraphs } from '@/components/Dashboard/DefaultGraphs';
import { generateColorArrayFromConfigs, generateRandomNumbers } from '@/components/Dashboard/Widgets/Reusable/chartUtils';
import { ConfigContext } from '@/contextAPI/configContext';
import { selectAllDashboardData, selectDashboardIsLoading } from '@/redux/selectors/dashboardSelectors';
import { OutstandingAssetsData, OutsandingAsset } from '@/redux/slices/slice_types';
import { useContext, useEffect, useState } from 'react';
import DashboardWidget, { WidgetData } from '../../DashboardWidget';
import { BarChartData } from '../../Reusable/BarChart';
import { useAppSelector } from '@/redux/hooks';

function getDayOfWeek(date: string) {
  const parts = date.split('-');
  const year = parseInt(parts[0]);
  const month = parseInt(parts[1]) - 1; // Months are zero-indexed
  const day = parseInt(parts[2]);

  const dateObject = new Date(year, month, day);
  const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayIndex = dateObject.getDay();

  return daysOfWeek[dayIndex];
}

const crateLabelFromKey = (key: string) => {
  return key.replace(/_/g, ' ').replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase();
  });
};

const parseOutstandingAssetsData = (data: OutstandingAssetsData): { labels: string[]; values: { label: string; data: number[] }[] } => {
  const labels: string[] = [];
  const attributeKeys = Object.keys(data[Object.keys(data)[0]]).filter((key) => key !== 'id' && key !== 'created_date');
  const valuesMap: Record<string, { label: string; data: number[] }> = {};

  attributeKeys.forEach((key) => {
    valuesMap[key] = {
      label: crateLabelFromKey(key),
      data: []
    };
  });

  Object.keys(data).forEach((date) => {
    labels.push(getDayOfWeek(date as string));
    attributeKeys.forEach((key) => {
      valuesMap[key].data.push(data[date][key as keyof OutsandingAsset] as number);
    });
  });

  const values = Object.keys(valuesMap).map((key) => valuesMap[key]);

  return { labels, values };
};

const OutstandingAssets = ({ title, caption }: WidgetData) => {
  const data = useAppSelector(selectAllDashboardData);
  const outstandingAssetsData = data.outstandingAssets;
  const dashboardConfig = useContext(ConfigContext)?.dashboardConfig;
  const isLoading = useAppSelector(selectDashboardIsLoading);

  const colors = generateColorArrayFromConfigs(
    7,
    dashboardConfig.chartColors,
    dashboardConfig.graphSettings['outstanding-assets'] ? dashboardConfig.graphSettings['outstanding-assets'].colors : []
  );

  // set to default
  const [barChartData, setBarChartData] = useState<BarChartData>({
    id: PreBuiltGraphs.OUTSTANDING_ASSETS,
    noDataMessage: 'No Data Available',
    labels: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    values: [
      {
        label: 'Unallocated Files',
        data: generateRandomNumbers(7, 25)
      },
      {
        label: 'Subs Not Complete',
        data: generateRandomNumbers(7, 25)
      },
      {
        label: 'Outstanding Material',
        data: generateRandomNumbers(7, 25)
      }
    ],
    colors: colors,
    legend: 'square',
    axes: {
      x: 'Date',
      y: 'Number of Orders'
    }
  });

  useEffect(() => {
    if (outstandingAssetsData && !isLoading && Object.keys(outstandingAssetsData).length !== 0) {
      const { labels, values } = parseOutstandingAssetsData(outstandingAssetsData);
      const newColors = generateColorArrayFromConfigs(
        labels.length,
        dashboardConfig.chartColors,
        dashboardConfig.graphSettings['outstanding-assets'] ? dashboardConfig.graphSettings['outstanding-assets'].colors : []
      );

      setBarChartData({
        ...barChartData,
        noDataMessage: null,
        labels: labels,
        values: values,
        colors: newColors
      });
    }
  }, [isLoading]);

  return <DashboardWidget title={title} caption={caption} data={barChartData} type="bar" />;
};

export default OutstandingAssets;
